import { useMemo } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Big } from 'big.js'
import { isBefore } from 'date-fns'
import { AnimatePresence, LayoutGroup } from 'framer-motion'
import { SubmitHandler, useForm } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'
import { generatePath, useNavigate, useParams } from 'react-router'
import { toast } from 'sonner'
import { z } from 'zod'

import { BusinessRoute } from '@/constants/paths'
import { queryKeys } from '@/constants/queryKeys'
import { useErrorToast } from '@/hooks/useErrorToast'
import { parseToFormat } from '@/lib/date'
import { queryClient } from '@/lib/queryClient'
import { getFullName } from '@/lib/typography'
import { getAnimationKey, removeEmptyFormFields } from '@/lib/utils'
import { AmountInput, ProgressButton } from '@/shared/components'
import {
  Button,
  DatePickerInput,
  Form,
  FormField,
  FormItem,
  FullScreen,
  MotionDiv,
  MotionFormItem,
  Skeleton,
  SlideInScreen,
  StickyContainer,
  Typography,
} from '@/shared/ui'

import {
  calculateLastPaymentQuote,
  endContract,
  getContractor,
  getEndContractQuote,
} from '../api'
import { LastCycleMethodSelect } from '../components'
import { ContractorFirstCycleMethod, ContractorPaymentType } from '../types'

const END_CONTRACT_FORM_ID = 'end-contract-form-id'

const endContractSchema = z
  .object({
    endDate: z.string().min(1, { message: 'validation.endDate.required' }),
    lastCyclePaymentMethod: z.string().optional(),
    lastPaymentAmount: z.string().optional(),
    paymentType: z.string().optional(),
    terminationPaymentDate: z.string().optional(),
  })
  .refine(
    (data) => {
      const { lastCyclePaymentMethod, lastPaymentAmount, paymentType } = data

      if (paymentType === ContractorPaymentType.PAY_AS_YOU_GO) {
        return true
      }

      if (lastCyclePaymentMethod === ContractorFirstCycleMethod.CUSTOM_AMOUNT) {
        const amount = lastPaymentAmount ? lastPaymentAmount : '0'

        return Big(amount).gt(0)
      }

      return true
    },
    {
      path: ['lastPaymentAmount'],
      message: 'validation.balance.minAmount',
    },
  )
  .refine(
    (data) => {
      const { terminationPaymentDate, lastCyclePaymentMethod, paymentType } =
        data

      if (paymentType === ContractorPaymentType.PAY_AS_YOU_GO) {
        return true
      }

      if (lastCyclePaymentMethod !== ContractorFirstCycleMethod.NO_PAYMENT) {
        return !!terminationPaymentDate
      }

      return true
    },
    {
      path: ['terminationPaymentDate'],
      message: 'validation.terminationPaymentDate.required',
    },
  )

type EndContractSchema = z.infer<typeof endContractSchema>

export const EndContract = () => {
  const intl = useIntl()
  const notifyError = useErrorToast()
  const navigate = useNavigate()

  const { mutateAsync, isPending, isSuccess } = useMutation({
    mutationFn: endContract,
  })

  const { id: contractorId } = useParams()

  const contractorQuery = useQuery({
    queryKey: [queryKeys.getContractor, contractorId],
    queryFn: () => getContractor({ id: contractorId ?? '' }),
    select: (data) => data?.data,
    enabled: !!contractorId,
  })

  const latestContract = contractorQuery.data?.contracts[0]

  const endContractQuoteQuery = useQuery({
    queryKey: [queryKeys.getEndContractQuote, latestContract?.id],
    queryFn: () =>
      getEndContractQuote({
        contractId: latestContract?.id ?? '',
      }),
    select: (data) => data?.data,
    enabled: !!latestContract?.id,
  })

  const form = useForm<EndContractSchema>({
    mode: 'onChange',
    resolver: zodResolver(endContractSchema),
    values: {
      endDate: '',
      lastCyclePaymentMethod: '',
      paymentType: latestContract?.paymentType,
      terminationPaymentDate: '',
      lastPaymentAmount: '',
    },
  })

  const lastPaymentQuoteQuery = useQuery({
    queryKey: [queryKeys.calculateLastPaymentQuote, contractorId],
    queryFn: () =>
      calculateLastPaymentQuote({
        contractId: latestContract?.id ?? '',
        endDate: parseToFormat(form.getValues('endDate')),
        lastCyclePaymentMethod:
          form.getValues('lastCyclePaymentMethod') !== ''
            ? (form.getValues(
                'lastCyclePaymentMethod',
              ) as ContractorFirstCycleMethod)
            : undefined,
        terminationPaymentDate:
          form.getValues('terminationPaymentDate') !== ''
            ? parseToFormat(form.getValues('terminationPaymentDate') as string)
            : null,
        lastPaymentAmount: null,
      }),
    select: (data) => data?.data,
    enabled:
      latestContract?.paymentType === ContractorPaymentType.FIXED_RATE &&
      !!latestContract?.id &&
      !!form.getValues('endDate') &&
      !!form.getValues('terminationPaymentDate'),
  })

  const onSubmit: SubmitHandler<EndContractSchema> = async (data) => {
    if (!latestContract?.id) {
      return
    }

    const endDate = endContractQuoteQuery.data?.earliestEndDate
    const terminationDate =
      endContractQuoteQuery.data?.earliestTerminationPaymentDate

    if (endDate && isBefore(new Date(parseToFormat(data.endDate)), endDate)) {
      toast.error(
        intl.formatMessage(
          {
            defaultMessage:
              'Termination date cannot be before {earliestEndDate}',
            id: 'contractor.endContract.terminationDateBeforeEarliestEndDate',
          },
          {
            earliestEndDate: intl.formatDate(endDate, {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
              timeZone: 'UTC',
            }),
          },
        ),
      )

      return
    }

    if (
      terminationDate &&
      data.terminationPaymentDate &&
      isBefore(
        new Date(parseToFormat(data.terminationPaymentDate)),
        terminationDate,
      )
    ) {
      toast.error(
        intl.formatMessage(
          {
            defaultMessage:
              'Termination payment date cannot be before {earliestTerminationPaymentDate}',
            id: 'contractor.endContract.terminationPaymentDateBeforeEarliestTerminationPaymentDate',
          },
          {
            earliestTerminationPaymentDate: intl.formatDate(terminationDate, {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
              timeZone: 'UTC',
            }),
          },
        ),
      )

      return
    }

    removeEmptyFormFields(data)

    try {
      await mutateAsync({
        endDate: parseToFormat(data.endDate),
        contractId: latestContract.id,

        lastCyclePaymentMethod: data.lastCyclePaymentMethod
          ? (data.lastCyclePaymentMethod as ContractorFirstCycleMethod)
          : undefined,

        lastPaymentAmount: data.lastPaymentAmount
          ? data.lastPaymentAmount
          : undefined,

        terminationPaymentDate: data.terminationPaymentDate
          ? parseToFormat(data.terminationPaymentDate)
          : undefined,
      })

      await queryClient.invalidateQueries({
        queryKey: [queryKeys.getContractor, contractorId],
      })

      await queryClient.invalidateQueries({
        queryKey: [queryKeys.getContractors],
      })

      await queryClient.invalidateQueries({
        queryKey: [queryKeys.getContractorPayments, contractorId],
      })

      toast.success(
        intl.formatMessage(
          {
            defaultMessage: 'Contract for {fullName} ended successfully',
            id: 'contractor.endContract.success',
          },
          {
            fullName: getFullName(contractorQuery.data?.personalDetails),
          },
        ),
      )

      navigate(
        generatePath(BusinessRoute.ContractorsDetails, {
          id: contractorId,
        }),
      )
    } catch (error) {
      notifyError(error)
    }
  }

  const earliestEndDate = useMemo(() => {
    return endContractQuoteQuery.data?.earliestEndDate
      ? new Date(endContractQuoteQuery.data.earliestEndDate)
      : new Date()
  }, [endContractQuoteQuery.data?.earliestEndDate])

  const earliestTerminationPaymentDate = useMemo(() => {
    return endContractQuoteQuery.data?.earliestTerminationPaymentDate
      ? new Date(endContractQuoteQuery.data.earliestTerminationPaymentDate)
      : new Date()
  }, [endContractQuoteQuery.data?.earliestTerminationPaymentDate])

  const proRata = useMemo(() => {
    const [lastPaymentCycle] =
      lastPaymentQuoteQuery.data?.paymentCycles.slice(-1) ?? []

    if (lastPaymentCycle) {
      return {
        isFetching: lastPaymentQuoteQuery.isFetching,
        amount: lastPaymentCycle.paymentAmount,
        lastPaymentDate: lastPaymentCycle.paymentDueDate,
      }
    }

    return undefined
  }, [
    lastPaymentQuoteQuery.data?.paymentCycles,
    lastPaymentQuoteQuery.isFetching,
  ])

  const showLastCycleMethodField =
    form.watch('paymentType') === ContractorPaymentType.FIXED_RATE

  const showCustomAmountField =
    form.watch('lastCyclePaymentMethod') ===
    ContractorFirstCycleMethod.CUSTOM_AMOUNT

  const showTerminationPaymentDate =
    form.watch('lastCyclePaymentMethod') !== '' &&
    form.watch('lastCyclePaymentMethod') !==
      ContractorFirstCycleMethod.NO_PAYMENT

  return (
    <FullScreen>
      <ProgressButton
        progress={50}
        to={generatePath(BusinessRoute.ContractorsDetails, {
          id: contractorId,
        })}
      />

      <SlideInScreen>
        <Typography text="center" variant="h3">
          <FormattedMessage
            id="contractor.endContract.title"
            defaultMessage="End contract"
          />
        </Typography>

        <div className="p-2" />

        <MotionDiv key={getAnimationKey(contractorQuery.isPending)}>
          {contractorQuery.data ? (
            <Typography text="center">
              <FormattedMessage
                id="contractor.endContract.subtitle"
                defaultMessage="We need a few more details to end the contract for {fullName}"
                values={{
                  fullName: getFullName(contractorQuery.data.personalDetails),
                }}
              />
            </Typography>
          ) : (
            <Skeleton className="mx-auto h-[21px] w-52" />
          )}
        </MotionDiv>

        <div className="p-6" />

        <Form {...form}>
          <form
            id={END_CONTRACT_FORM_ID}
            onSubmit={form.handleSubmit(onSubmit)}
          >
            <div className="flex flex-col gap-3">
              <FormField
                control={form.control}
                name="endDate"
                render={({ field }) => (
                  <FormItem>
                    <DatePickerInput
                      calendarProps={{
                        disabled: { before: earliestEndDate },
                      }}
                      placeholder={intl.formatMessage({
                        defaultMessage: 'Termination date',
                        id: 'label.terminationDate',
                      })}
                      {...field}
                    />
                  </FormItem>
                )}
              />

              <LayoutGroup>
                <FormField
                  control={form.control}
                  name="lastCyclePaymentMethod"
                  render={({ field }) => (
                    <AnimatePresence>
                      {showLastCycleMethodField ? (
                        <MotionFormItem layout>
                          <LastCycleMethodSelect
                            value={field.value}
                            onChange={field.onChange}
                            proRata={proRata}
                          />
                        </MotionFormItem>
                      ) : null}
                    </AnimatePresence>
                  )}
                />

                <FormField
                  control={form.control}
                  name="lastPaymentAmount"
                  render={({ field }) => (
                    <AnimatePresence>
                      {showCustomAmountField ? (
                        <MotionFormItem layout>
                          <AmountInput
                            placeholder={intl.formatMessage({
                              defaultMessage:
                                'Custom last cycle payment amount',
                              id: 'label.customLastCyclePaymentAmount',
                            })}
                            value={field.value}
                            onChange={field.onChange}
                          />
                        </MotionFormItem>
                      ) : null}
                    </AnimatePresence>
                  )}
                />

                <FormField
                  control={form.control}
                  name="terminationPaymentDate"
                  render={({ field }) => (
                    <AnimatePresence>
                      {showTerminationPaymentDate ? (
                        <MotionFormItem layout>
                          <DatePickerInput
                            calendarProps={{
                              disabled: {
                                before: earliestTerminationPaymentDate,
                              },
                            }}
                            placeholder={intl.formatMessage({
                              defaultMessage: 'Termination payment date',
                              id: 'label.terminationPaymentDate',
                            })}
                            {...field}
                            value={field.value ?? ''}
                          />
                        </MotionFormItem>
                      ) : null}
                    </AnimatePresence>
                  )}
                />
              </LayoutGroup>
            </div>
          </form>
        </Form>

        <StickyContainer>
          <Button
            form={END_CONTRACT_FORM_ID}
            onClick={form.handleSubmit(onSubmit)}
            loading={isPending || isSuccess}
            disabled={!form.formState.isValid || isPending || isSuccess}
            type="submit"
            width="full"
          >
            <FormattedMessage
              id="action.endContract"
              defaultMessage="End contract"
            />
          </Button>
        </StickyContainer>
      </SlideInScreen>
    </FullScreen>
  )
}
